import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface Query {
  id: string;
  title: string;
  author: string;
  queryPreview: string;
  genre: string;
  status: string;
  receivedDate: string;
  wordCount: number;
  firstPages: boolean;
}

export const QueryInboxPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [queries, setQueries] = useState<Query[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQueries();
  }, []);

  const formatGenre = (genre: string) => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery/Thriller',
      THRILLER: 'Mystery/Thriller',
      ROMANCE: 'Romance',
      HISTORICAL_FICTION: 'Historical Fiction',
      LITERARY_FICTION: 'Literary Fiction',
      CONTEMPORARY: 'Contemporary',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      HORROR: 'Horror',
      WOMENS_FICTION: 'Women\'s Fiction',
      COMMERCIAL_FICTION: 'Commercial Fiction',
    };
    return genreMap[genre] || genre;
  };

  const fetchQueries = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.queries.inbox, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch queries');
        setLoading(false);
        return;
      }

      const transformedQueries = data.queries.map((q: any) => {
        // Map backend status to frontend status
        let frontendStatus = 'unread';
        if (q.status === 'PENDING') {
          frontendStatus = 'unread';
        } else if (q.status === 'REVIEWING') {
          frontendStatus = 'read';
        } else if (q.status === 'REQUESTED' || q.status === 'OFFERED') {
          frontendStatus = 'requested';
        } else if (q.status === 'REJECTED') {
          frontendStatus = 'passed';
        }

        return {
          id: q.id,
          author: q.writer?.displayName || 'Unknown Author',
          title: q.manuscript?.title || 'Unknown Title',
          genre: formatGenre(q.manuscript?.primaryGenre || ''),
          wordCount: q.manuscript?.wordCount || 0,
          receivedDate: new Date(q.createdAt).toISOString().split('T')[0],
          status: frontendStatus,
          queryPreview: q.queryLetter || 'No query letter provided',
          firstPages: !!q.firstPages,
        };
      });

      setQueries(transformedQueries);
      setLoading(false);
    } catch (err) {
      console.error('Fetch queries error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const genres = ['All Genres', 'Literary Fiction', 'Commercial Fiction', 'Women\'s Fiction', 'Mystery/Thriller', 'Science Fiction', 'Fantasy'];
  const statuses = [
    { value: 'all', label: 'All Queries' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
    { value: 'requested', label: 'Requested' },
    { value: 'passed', label: 'Passed' },
  ];
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'author', label: 'Author (A-Z)' },
    { value: 'title', label: 'Title (A-Z)' },
  ];

  // Filter queries
  const filteredQueries = queries.filter((query: Query) => {
    const matchesSearch = query.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         query.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         query.queryPreview.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = genreFilter === 'all' || query.genre === genreFilter;
    const matchesStatus = statusFilter === 'all' || query.status === statusFilter;
    return matchesSearch && matchesGenre && matchesStatus;
  });

  // Sort queries
  const sortedQueries = [...filteredQueries].sort((a: Query, b: Query) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.receivedDate).getTime() - new Date(a.receivedDate).getTime();
      case 'oldest':
        return new Date(a.receivedDate).getTime() - new Date(b.receivedDate).getTime();
      case 'author':
        return a.author.localeCompare(b.author);
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: 'primary' | 'success' | 'warning' | 'error' | 'default', text: string }> = {
      unread: { variant: 'warning', text: 'Unread' },
      read: { variant: 'default', text: 'Read' },
      requested: { variant: 'success', text: 'Requested' },
      passed: { variant: 'error', text: 'Passed' },
    };
    const config = statusConfig[status] || statusConfig.read;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const handleSelectQuery = (queryId: string) => {
    if (selectedQueries.includes(queryId)) {
      setSelectedQueries(selectedQueries.filter(id => id !== queryId));
    } else {
      setSelectedQueries([...selectedQueries, queryId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedQueries.length === sortedQueries.length) {
      setSelectedQueries([]);
    } else {
      setSelectedQueries(sortedQueries.map(q => q.id));
    }
  };

  const handleBulkAction = (action: string) => {
    if (selectedQueries.length === 0) {
      window.alert('Please select at least one query');
      return;
    }
    console.log(`Bulk action: ${action} on queries:`, selectedQueries);
    window.alert(`${action} applied to ${selectedQueries.length} ${selectedQueries.length === 1 ? 'query' : 'queries'}`);
    setSelectedQueries([]);
  };

  const unreadCount = queries.filter((q: Query) => q.status === 'unread').length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Query Inbox
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            {unreadCount} unread {unreadCount === 1 ? 'query' : 'queries'}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading queries...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchQueries}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Search and Filters */}
        {!loading && !error && (
        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {/* Search Bar */}
            <Input
              type="text"
              placeholder="Search by title, author, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            {/* Filters Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
            }}>
              <Select
                label="Genre"
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value)}
                options={genres.map(genre => ({
                  value: genre === 'All Genres' ? 'all' : genre,
                  label: genre,
                }))}
              />

              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={statuses}
              />

              <Select
                label="Sort By"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={sortOptions}
              />
            </div>
          </div>
        </Card>
        )}

        {/* Bulk Actions */}
        {selectedQueries.length > 0 && (
          <Card style={{
            backgroundColor: theme.colors.primaryLight,
            marginBottom: '24px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
            }}>
              <div style={{
                fontSize: '15px',
                color: theme.colors.textPrimary,
              }}>
                {selectedQueries.length} {selectedQueries.length === 1 ? 'query' : 'queries'} selected
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                <Button size="small" variant="outline" onClick={() => handleBulkAction('Mark as Read')}>
                  Mark as Read
                </Button>
                <Button size="small" variant="outline" onClick={() => handleBulkAction('Archive')}>
                  Archive
                </Button>
                <Button size="small" variant="ghost" onClick={() => setSelectedQueries([])}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results Count and Select All */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}>
          <div style={{
            fontSize: '16px',
            color: theme.colors.textSecondary,
          }}>
            {sortedQueries.length} {sortedQueries.length === 1 ? 'query' : 'queries'} found
          </div>
          <button
            onClick={handleSelectAll}
            style={{
              background: 'none',
              border: 'none',
              color: theme.colors.primary,
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {selectedQueries.length === sortedQueries.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Query List */}
        {sortedQueries.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                No queries found matching your criteria
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setGenreFilter('all');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {sortedQueries.map((query) => (
              <Card
                key={query.id}
                style={{
                  backgroundColor: query.status === 'unread' ? theme.colors.primaryLight : theme.colors.white,
                }}
              >
                <div style={{
                  display: 'flex',
                  gap: '16px',
                }}>
                  {/* Checkbox */}
                  <div style={{
                    paddingTop: '4px',
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedQueries.includes(query.id)}
                      onChange={() => handleSelectQuery(query.id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                    }}>
                      <h3 style={{
                        fontSize: '22px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        margin: 0,
                      }}>
                        {query.title}
                      </h3>
                      {getStatusBadge(query.status)}
                    </div>

                    <p style={{
                      fontSize: '16px',
                      color: theme.colors.textSecondary,
                      marginBottom: '12px',
                    }}>
                      by {query.author}
                    </p>

                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '12px',
                      flexWrap: 'wrap',
                    }}>
                      <span>{query.genre}</span>
                      <span>•</span>
                      <span>{query.wordCount.toLocaleString()} words</span>
                      <span>•</span>
                      <span>Received: {query.receivedDate}</span>
                      {query.firstPages && (
                        <>
                          <span>•</span>
                          <span>📄 First pages included</span>
                        </>
                      )}
                    </div>

                    <p style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: theme.colors.textPrimary,
                      marginBottom: '16px',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {query.queryPreview}
                    </p>

                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}>
                      <Button
                        size="small"
                        onClick={() => window.location.href = `/agent/queries/${query.id}`}
                      >
                        Review Query
                      </Button>
                      {query.status === 'unread' && (
                        <Button
                          size="small"
                          variant="outline"
                          onClick={() => console.log('Mark as read:', query.id)}
                        >
                          Mark as Read
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
